import { RiArrowDownSFill } from "@itandi/itandi-bb-ui";
import cx from "clsx";
import type { ReactNode } from "react";
import React, { useContext } from "react";
import { Portal } from "react-overlays";
import { usePopper } from "react-popper";
import { useClickAway } from "~/hooks/useClickAway";
import Styles from "./Dropdown.module.scss";

type Props = {
	children?: ReactNode;
	className?: string;
	disabled?: boolean;
	noBorder?: boolean;
	placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
	size?: "md" | "sm";
	title: string | ReactNode;
	useCaret?: boolean;
	variant?: "primary" | "secondary" | "normal" | "link";
};

type DropdownState = {
	setShow?: (show: boolean) => void;
	show: boolean;
};
export const DropdownContext = React.createContext<DropdownState>({
	setShow: undefined,
	show: false
});

export const useDropdownContext = (): DropdownState =>
	useContext<DropdownState>(DropdownContext);

export function Dropdown({
	title,
	children,
	className = "",
	disabled = false,
	placement = "bottom-end",
	size = "md",
	variant = "primary",
	noBorder = false,
	useCaret = true
}: Props) {
	const [show, setShow] = React.useState<boolean>(false);
	const referenceRef = React.useRef<HTMLButtonElement | null>(null);
	const popperRef = React.useRef<HTMLDivElement | null>(null);
	useClickAway(popperRef, referenceRef, (): void => {
		setShow(false);
	});
	const { styles, attributes, update } = usePopper(
		referenceRef.current,
		popperRef.current,
		{
			placement
		}
	);
	React.useEffect(() => {
		if (update) update();
	}, [show, update]);
	const onClickToggle = (): void => {
		setShow(!show);
	};

	const variantStyle = ((): string => {
		switch (variant) {
			case "primary":
				return Styles.Primary;
			case "secondary":
				return Styles.Secondary;
			case "normal":
				return Styles.Normal;
			case "link":
				return Styles.Link;
			default:
				return "";
		}
	})();
	const sizeStyle = ((): string => {
		switch (size) {
			case "md":
				return Styles.Medium;
			case "sm":
				return Styles.Small;
			default:
				return "";
		}
	})();
	const container = ((): HTMLElement | null => {
		if (typeof document === "undefined") {
			return null;
		}
		return document.body;
	})();
	const providerValue = React.useMemo(
		() => ({ setShow, show }),
		[show, setShow]
	);
	return (
		<DropdownContext.Provider value={providerValue}>
			<button
				aria-expanded="false"
				aria-haspopup="true"
				className={cx(
					className,
					Styles.DropdownToggle,
					variantStyle,
					sizeStyle,
					show ? Styles.ShowStyle : "",
					noBorder ? Styles.NoBorder : ""
				)}
				data-toggle="dropdown"
				disabled={disabled}
				onClick={onClickToggle}
				ref={referenceRef}
				type="button"
			>
				<span className={Styles.DropdownTitle}>{title}</span>
				{useCaret ? <RiArrowDownSFill /> : null}
			</button>
			<Portal container={container}>
				<div
					className={cx(Styles.DropdownPopper, show ? "" : Styles.Hide)}
					ref={popperRef}
					style={styles.popper}
					{...attributes.popper}
				>
					<div className={show ? "" : Styles.Hide}>{children}</div>
				</div>
			</Portal>
		</DropdownContext.Provider>
	);
}
